import { useEffect, useId, useState } from 'react';
import { Alert, Spin } from 'antd';
import { useTranslation } from 'react-i18next';

// The editor script is served by the document server itself; load it once per
// server address and share the promise between editors.
const scriptLoads = new Map();

const loadEditorScript = (documentServerUrl) => {
  if (window.DocsAPI) return Promise.resolve();
  if (!scriptLoads.has(documentServerUrl)) {
    scriptLoads.set(
      documentServerUrl,
      new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `${documentServerUrl}/web-apps/apps/api/documents/api.js`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
          scriptLoads.delete(documentServerUrl);
          reject(new Error('ONLYOFFICE script failed to load'));
        };
        document.body.appendChild(script);
      })
    );
  }
  return scriptLoads.get(documentServerUrl);
};

/**
 * Word document editor (ONLYOFFICE) for a document under review. The server
 * hands over a signed `config`; edits are saved by the document server straight
 * back to the API, so this component only mounts and tears down the editor.
 *
 * @param {string}   documentServerUrl - Browser-facing document server address.
 * @param {Object}   config            - Signed editor configuration.
 * @param {Function} [onReady]         - Called once the document is open.
 */
export default function OnlyOfficeEditor({ documentServerUrl, config, onReady }) {
  const { t } = useTranslation('forms');
  const elementId = `onlyoffice-${useId().replace(/:/g, '')}`;
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let editor = null;
    let cancelled = false;

    loadEditorScript(documentServerUrl)
      .then(() => {
        if (cancelled) return;
        editor = new window.DocsAPI.DocEditor(elementId, {
          ...config,
          events: {
            onDocumentReady: () => {
              setStatus('ready');
              onReady?.();
            },
            onError: () => setStatus('error'),
          },
        });
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      try {
        editor?.destroyEditor();
      } catch {
        // The editor frame may already be gone with the modal.
      }
    };
    // The config is signed per draft; a new draft remounts the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentServerUrl, config?.document?.key]);

  return (
    <div className="relative h-full w-full">
      {status === 'loading' && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
          <Spin tip={t('wordEditorLoading', 'Otvaram dokument…')} />
        </div>
      )}
      {status === 'error' && (
        <Alert
          type="error"
          showIcon
          className="!mb-2"
          message={t(
            'wordEditorFailed',
            'Word editor nije mogao da se otvori. Proverite vezu i pokušajte ponovo.'
          )}
        />
      )}
      <div id={elementId} />
    </div>
  );
}
